import React from 'react';

/* A survey's answers as numbers: one block per question, bars scaled to the tallest.

   Shared, because the same numbers are read in two places and must read identically: on the host
   screen, narrowed to tonight, and in the question library, rolled up across every event the set
   was ever asked at. It lived on the host screen and the library had an older copy of half of it;
   the quiz line below is exactly the kind of thing that would have landed in one and not the
   other.

   The `hv-*` classes come from HostEvent.css, which both screens import. */

/* --- one question's numbers ------------------------------------------------------------- */

const QuestionResult = ({ q, t }) => {
  const options = q.options || [];
  const texts = q.texts || [];
  const dist = q.distribution || [];
  // The tallest bar sets the scale. Without it a question where every option scored 2 renders as
  // four full-width bars and says nothing.
  const peak = Math.max(1, ...options.map((o) => o.count), ...dist.map((d) => d.count));

  return (
    <div className="hv-q">
      <div className="hv-q__label">{q.label}</div>
      <div className="hv-q__n">{t('surveyAnswers', { n: q.answers || 0 })}</div>

      {/* ★ A quiz question, and only a quiz question, says what the right answer was and how
          many found it. The server leaves these off entirely otherwise, so an ordinary survey
          never renders "0 correct" against a question that was never a test. */}
      {q.correct_answer && (
        <div className="hv-q__quiz">
          {t('correctAnswerIs', { answer: q.correct_answer })}
          {' · '}
          {t('gotItRight', { n: q.correct || 0, pct: q.correct_pct || 0 })}
          {' · '}
          {t('nPoints', { n: q.points || 0 })}
        </div>
      )}

      {options.map((o) => (
        <div key={o.value} className={`hv-bar ${o.is_correct ? 'hv-bar--right' : ''}`}>
          <span className="hv-bar__label">{o.is_correct ? '✓ ' : ''}{o.value}</span>
          <span className="hv-bar__track">
            <span className="hv-bar__fill" style={{ width: `${(o.count / peak) * 100}%` }} />
          </span>
          <span className="hv-bar__n">{o.count} · {o.pct}%</span>
        </div>
      ))}

      {q.mean != null && (
        <>
          <div className="hv-mean">
            {q.mean}
            {q.maximum ? <span className="hv-mean__max"> / {q.maximum}</span> : null}
          </div>
          {dist.map((d) => (
            <div key={d.value} className="hv-bar">
              <span className="hv-bar__label">{d.value}</span>
              <span className="hv-bar__track">
                <span className="hv-bar__fill" style={{ width: `${(d.count / peak) * 100}%` }} />
              </span>
              <span className="hv-bar__n">{d.count}</span>
            </div>
          ))}
        </>
      )}

      {texts.map((text, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className="hv-text">“{text}”</div>
      ))}
      {q.texts_truncated > 0 && (
        <div className="hv-q__n">{t('andMoreAnswers', { n: q.texts_truncated })}</div>
      )}
    </div>
  );
};

const SurveyResults = ({ results, t }) => {
  if (!results) return <span className="auth__spinner" />;
  if (!results.responses) return <p className="ce-note">{t('noAnswersYet')}</p>;
  return (
    <>
      <p className="hv-total">{t('surveyResponses', { n: results.responses })}</p>
      {(results.questions || []).map((q) => <QuestionResult key={q.id} q={q} t={t} />)}
    </>
  );
};

export { QuestionResult };
export default SurveyResults;
